import React, { useCallback, useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Tooltip from "../../atoms/Tooltip/ImperativeTooltip";
import {
  setCardsDensity,
  setCardsLayout,
  setViewAllCards,
} from "../../../state/actions/Cards";
import {
  getCardsIsCompact,
  getCardsIsOnSide,
  getCardsIsViewAll,
} from "../../../state/selectors/CardsUI.selectors";
import ls from "../../../common/helpers/localStorageActions";
import store from "../../../state/store";
import { Toggle } from "../../atoms";
import { eventTrack } from "../../../common/helpers/helpers";
import { ANALYTICS_CATEGORIES } from "../common/constants";
import CogIcon from "../../icons/CogIcon";

const LayoutOptions = ({ isLoading, isCompact, isOnSide, isViewAll }) => {
  const [isOptionsVisible, setIsOptionsVisible] = useState(false);
  const [isCompactLocal, setIsCompactLocal] = useState(isCompact);
  const [isOnSideLocal, setIsOnSideLocal] = useState(isOnSide);
  const [isViewAllLocal, setIsViewAllLocal] = useState(isViewAll);
  const optionsRef = useRef(null);
  const closeOptions = useCallback(() => {
    setIsOptionsVisible(false);
  }, []);

  useEffect(() => {
    if (!isOptionsVisible) {
      // Reset redux values on cancel to show
      // latest preferences when we click again
      setIsCompactLocal(isCompact);
      setIsOnSideLocal(isOnSide);
      setIsViewAllLocal(isViewAll);
      window.dispatchEvent(new Event("resize"));
    }
  }, [isOptionsVisible]);

  const trackEventName = useCallback((option) => {
    // raise event for tracking
    eventTrack(ANALYTICS_CATEGORIES.CARDS_UI, "Preference Setting", option);
  }, []);

  const onSaveOptions = useCallback(() => {
    const compactOption = isCompactLocal ? "Compact" : "Comfort";
    const viewAllOption = isViewAllLocal ? "View All" : "View One";
    const sideOption = isOnSideLocal ? "On Side" : "On Top";

    if (isCompact !== isCompactLocal) {
      ls.setCardsComfortView(isCompactLocal);
      store.dispatch(setCardsDensity(isCompactLocal));
      trackEventName(compactOption);
      window.dispatchEvent(new Event("resize"));
    }

    if (isOnSide !== isOnSideLocal) {
      trackEventName(sideOption);
      ls.setCardsLayout(isOnSideLocal);
      store.dispatch(setCardsLayout(isOnSideLocal));
      window.dispatchEvent(new Event("resize"));
    }

    if (isViewAll !== isViewAllLocal) {
      trackEventName(viewAllOption);
      ls.setViewAllCards(isViewAllLocal);
      store.dispatch(setViewAllCards(isViewAllLocal));
      window.dispatchEvent(new Event("resize"));
    }
    if ([isViewAllLocal, isOnSideLocal, isCompactLocal].every((e) => e)) {
      trackEventName("Old layout enabled");
    }
  }, [isCompactLocal, isOnSideLocal, isViewAllLocal, isOptionsVisible]);

  return isLoading ? null : (
    <React.Fragment>
      <div
        className={
          __IS_COMPONENTS_PORTAL__
            ? "tw-flex  tw-justify-center  tw-border-primary tw-rounded-full  wizard-layoutOptions"
            : "tw-flex tw-items-center tw-justify-center tw-h-6 tw-w-6 tw-border-primary tw-rounded-full tw-m-2 tw-my-1 wizard-layoutOptions"
        }
        type="submit"
        onClick={() => setIsOptionsVisible(true)}
        ref={optionsRef}
      >
        <CogIcon size={16} className="tw-text-primary tw-fill-current" />
      </div>

      {isOptionsVisible ? (
        <Tooltip
          onClose={closeOptions}
          anchor={optionsRef.current}
          shouldFollowObjectOnScroll={false}
          contentClassName={`tw-w-min-64 tw-text-blueGray-800 ${
            __IS_COMPONENTS_PORTAL__ ? "layout-options-container-clinical" : ""
          }`}
          ContentRenderer={() => (
            <div className="tw-text-xs tw-text-blueGray-700 tw-p-2">
              <Toggle
                className="tw-px-0 tw-py-2"
                isChecked={isCompactLocal}
                onChange={(e) => setIsCompactLocal(e)}
              >
                <div className="tw-px-2">Compact View</div>
              </Toggle>
              {!__IS_COMPONENTS_PORTAL__ && (
                <Toggle
                  className="tw-px-0 tw-py-2"
                  isChecked={isOnSideLocal}
                  onChange={(e) => setIsOnSideLocal(e)}
                >
                  <div className="tw-px-2">Sidebar View</div>
                </Toggle>
              )}
              {!__IS_COMPONENTS_PORTAL__ && (
                <Toggle
                  className="tw-px-0 tw-py-2"
                  isChecked={isViewAllLocal}
                  onChange={(e) => setIsViewAllLocal(e)}
                >
                  <div className="tw-px-2">View All Data</div>
                </Toggle>
              )}
              <div className="tw-text-center tw-mt-2">
                <button
                  type="button"
                  className="tw-font-bold tw-bg-blueGray-200 tw-border-0 hover:tw-bg-green-100 tw-rounded-md tw-px-6 tw-py-2"
                  onClick={() => {
                    onSaveOptions();
                    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        />
      ) : null}
    </React.Fragment>
  );
};

LayoutOptions.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  isCompact: PropTypes.bool.isRequired,
  isOnSide: PropTypes.bool.isRequired,
  isViewAll: PropTypes.bool.isRequired,
};

LayoutOptions.defaultProps = {};

const mapStateToProps = (state) => {
  return {
    isLoading: state.loading,
    isCompact: getCardsIsCompact(state),
    isOnSide: getCardsIsOnSide(state),
    isViewAll: getCardsIsViewAll(state),
  };
};

export default connect(mapStateToProps, {})(LayoutOptions);
